/**
 * Highcharts Configuration Module
 * This module provides custom settings and configuration for Highcharts
 * and interfaces with highchart-helpers.js for additional functionality
 * author: martinv@equisolve.com
 */
// ============== Settings Configuration ==============
const getCSSVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

let settings = {
    // Pie & Doughnut
    colors: [
        getCSSVar('--chart-pie-donut-color-1'),
        getCSSVar('--chart-pie-donut-color-2'),
        getCSSVar('--chart-pie-donut-color-3'),
        getCSSVar('--chart-pie-donut-color-4'),
        getCSSVar('--chart-pie-donut-color-5'),
        getCSSVar('--chart-pie-donut-color-6'),
        getCSSVar('--chart-pie-donut-color-7'),
        getCSSVar('--chart-pie-donut-color-8'),
        getCSSVar('--chart-pie-donut-color-9'),
        getCSSVar('--chart-pie-donut-color-10'),
        getCSSVar('--chart-pie-donut-color-11'),
        getCSSVar('--chart-pie-donut-color-12'),
        getCSSVar('--chart-pie-donut-color-13'),
        getCSSVar('--chart-pie-donut-color-14'),
        getCSSVar('--chart-pie-donut-color-15'),
        getCSSVar('--chart-pie-donut-color-16'),
    ],
    // Bar & Grouped Charts
    barColors: [
        getCSSVar('--chart-bar-color-1'),
        getCSSVar('--chart-bar-color-2'),
        getCSSVar('--chart-bar-color-3'),
        getCSSVar('--chart-bar-color-4'),
        getCSSVar('--chart-bar-color-5'),
        getCSSVar('--chart-bar-color-6'),
    ],
    // Stacked Bar Charts
    stackedBarColors: [
        getCSSVar('--chart-stacked-bar-color-1'),
        getCSSVar('--chart-stacked-bar-color-2'),
        getCSSVar('--chart-stacked-bar-color-3'),
        getCSSVar('--chart-stacked-bar-color-4'),
        getCSSVar('--chart-stacked-bar-color-5'),
        getCSSVar('--chart-stacked-bar-color-6'),
    ],
    // CAGR Charts
    cagrBarColors: [
        getCSSVar('--chart-cagr-bar-color-1'),
        getCSSVar('--chart-cagr-bar-color-2'),
        getCSSVar('--chart-cagr-bar-color-3'),
        getCSSVar('--chart-cagr-bar-color-4'),
        getCSSVar('--chart-cagr-bar-color-5'),
        getCSSVar('--chart-cagr-bar-color-6'),
    ],
    // Line Charts
    lineColors: [
        getCSSVar('--chart-line-color-1'),
        getCSSVar('--chart-line-color-2'),
        getCSSVar('--chart-line-color-3'),
        getCSSVar('--chart-line-color-4'),
        getCSSVar('--chart-line-color-5'),
        getCSSVar('--chart-line-color-6'),
    ],
    // Line Chart Point Styles
    // Custom symbols are defined in the highcharts-custom-symbol file
    pointStyles: [
        'circle',
        'square',
        'triangle',
        'star',
        'diamond',
        'pentagon',
        'cross',
        'crossRot',
        'rectRot',
        'line',
        'rectRounded',
        'dash',
    ],
    rotation: 0,
    cutout: '50%',
    legendPosition: 'bottom',
    legendAlign: 'start',
    abbreviateNumber: true,
    pointRadius: 5,
    pointBorderWidth: 2,
    cubicInterpolationMode: 'monotone',
    font_family: "'Mulish', sans-serif",
    axisLabelColor: getCSSVar('--chart-axis-label-color'),
    axisLineColor: getCSSVar('--chart-axis-line-color'),
    gridLineColor: getCSSVar('--chart-grid-line-color'),
};

// ============== Helper Functions ==============
const helpers = {
    getUnitSuffixes(unit) {
        const unitSuffixes = {
            percentage: { full: '{y}%', prefix: '', suffix: '%' },
            currency: { full: '${y}', prefix: '$', suffix: '' },
            million: { full: '{y}M', prefix: '', suffix: 'M' },
            billion: { full: '{y}B', prefix: '', suffix: 'B' }
        };
        return unitSuffixes[unit] || { full: '{y}', prefix: '', suffix: '' };
    },

    calculateYAxisMax(maxValue, tickStep, addExtraSpace, isStacked) {
        const baseMax = Math.ceil(Number(maxValue) / Number(tickStep)) * Number(tickStep);
        const extraSpaceFactor = addExtraSpace 
            ? (isStacked ? 2 : 0.5)
            : (isStacked ? 2 : 0);
        return baseMax + (Number(tickStep) * extraSpaceFactor);
    },

    // this function rename old chartjs type parameters
    getChartType(type) {
        if (!type || type === 'bar') return 'column';
        if (type === 'doughnut') return 'pie';
        return type;
    }
};

// ============== Chart Configuration ==============
const chartConfig = {
    getBaseConfig(cd, hasMultipleDatasets) {
        return {
            lang: {
                accessibility: {
                    chartContainerLabel: "",
                    svgContainerLabel: `Interactive chart for ${cd.title}, ${cd.subtitle}`,
                    screenReaderSection: {
                        endOfChartMarker: `End of interactive chart for ${cd.title}, ${cd.subtitle}`,
                        annotations: {
                            descriptionSinglePoint: "{annotationText}"
                        }
                    },
                    axis: {
                        rangeFromTo: `Data ranges from {rangeFrom} to {rangeTo}`,
                    }
                }
            },
            chart: {
                type: helpers.getChartType(cd.type),
                height: 300,
                styledMode: false,
                className: `eqchart-${cd.id}`,
                style: { fontFamily: settings.font_family },
            },
            credits: { enabled: false },
            title: { text: "" },
            legend: { enabled: false },
            data: {
                table: 'datatable',
                labels: cd.labels,
                datasets: cd.datasets
            },
            styles: {
                fontFamily: settings.font_family
            },
            accessibility: {
                enabled: true,
                screenReaderSection: {
                    beforeChartFormat: '<div>{typeDescription}</div><div>{chartSubtitle}</div><div>{chartLongdesc}</div><div>{playAsSoundButton}</div><div>{viewTableButton}</div><div>{xAxisDescription}</div><div>{yAxisDescription}</div><div>{annotationsTitle}{annotationsList}</div>',
                    endOfChartMarker: '',
                    afterChartFormat: ''
                },
                point: {
                    valueDescriptionFormat: hasMultipleDatasets ? `{xDescription}{separator}{value} ${cd.unit}` : `{xDescription}{separator}{value}`,
                },
                series: {
                    descriptionFormatter: function (series) {
                        // Generate custom description with chart number
                        const chartNumber = series.chart.userOptions.chartNumber || 1;
                        const seriesName = series.name || 'Series';
                        const seriesType = series.type || 'column';
                        const dataLength = series.data ? series.data.length : 0;
                        const totalSeries = series.chart.series.length;
                        const seriesIndex = series.index + 1;
                        
                        if (totalSeries > 1) {
                            return `${seriesName}, This is chart number ${chartNumber} on this page, with ${seriesType} series ${seriesIndex} of ${totalSeries} and ${dataLength} data groups.`;
                        } else {
                            return `${seriesName}, This is chart number ${chartNumber} on this page, with ${seriesType} series and ${dataLength} data points.`;
                        }
                    }
                },
                keyboardNavigation:{
                    enabled: true,
                    focusBorder:{
                        enabled: true,
                        hideBrowserFocusOutline:false,
                        order: [
                            'container', 'series', 'zoom', 'rangeSelector',
                            'navigator', 'legend', 'chartMenu'
                        ],
                        wrapAround: true
                    }
                },
            }
        };
    },

    getAxisConfig(cd, suffixUnit) {
        return {
            yAxis: {
                title: {
                    text: cd.y_axis_title,
                    style: { fontSize: '0.8em' }
                },
                labels: {
                    formatter: function () {
                        return `${Highcharts.numberFormat(this.value, -1, '.', ' ')}${suffixUnit}`;
                    },
                    style: { color: settings.axisLabelColor, fontFamily: 'Helvetica', fontSize: '10px' }
                },
                stackLabels: { enabled: false },
                lineColor: settings.axisLineColor,
                lineWidth: 0,
                gridLineColor: settings.gridLineColor,
                gridLineWidth: 1,
            },
            xAxis: {
                lineColor: settings.axisLineColor,
                categories: cd.labels,
                gridLineColor: settings.gridLineColor,
                labels: {
                    style: { color: settings.axisLabelColor, fontFamily: 'Helvetica', fontSize: '10px' },
                    formatter: function() {
                        const monthAbbreviations = {
                            'January': 'Jan', 'February': 'Feb', 'March': 'Mar',
                            'April': 'Apr', 'May': 'May', 'June': 'Jun',
                            'July': 'Jul', 'August': 'Aug', 'September': 'Sep',
                            'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
                        };
                        const label = this.axis.defaultLabelFormatter.call(this);
                        return monthAbbreviations[label] || label;
                    }
                }
            }
        };
    },

    getTooltipConfig(suffixFullFormat, hasMultipleDatasets) {
        return {
            tooltip: {
                formatter: function () {
                    // Currency-style: thousands separators, with two decimals only when the value has a fractional part
                    const decimals = Number(this.y) % 1 === 0 ? 0 : 2;
                    const formattedValue = suffixFullFormat.replace(
                        /\{y\}/g,
                        Highcharts.numberFormat(this.y, decimals, '.', ',')
                    );
                    const pointColor = this.point.color || this.series.color;
                    const seriesName = hasMultipleDatasets
                        ? `<span style="font-size: 0.9em; font-weight: 900;">${this.series.name}</span><br/>`
                        : '';
                    return `${seriesName}<span style="font-size: 0.8em">${this.key}</span><br/>
                        <span style="color:${pointColor}">\u25CF</span> ${formattedValue}`;
                }
            }
        };
    },

    getPlotOptionsConfig(cd, prefixUnit, suffixUnit) {
        return {
            plotOptions: {
                column: {
                    borderRadius: 0,
                    groupPadding: 0.1,
                    pointPadding: 0.05,
                    stacking: cd.stacked ? 'normal' : undefined,
                    dataLabels: { enabled: cd.stacked }
                },
                pie: {
                    borderWidth: 3
                },
                series: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    showInLegend: true,
                    dataLabels: [
                        {
                            enabled: false,
                            distance: 20
                        },
                        {
                            enabled: true,
                            distance: -40,
                            format: `${prefixUnit}{y}${suffixUnit}`,
                            style: {
                                fontSize: '0.9em',
                                textOutline: 'none',
                                opacity: 1
                            },
                            filter: {
                                operator: '>',
                                property: cd.unit,
                                value: 10
                            }
                        }
                    ],
                    colorByPoint: false,
                    accessibility: {
                        enabled: true,
                        description: '' // Disable default description to prevent duplication
                    }
                }
            }
        };
    }
};

// ============== Main Configuration Function ==============
function prep_config(cd) {
    // Get unit suffixes
    const { full: suffixFullFormat, prefix: prefixUnit, suffix: suffixUnit } = cd.custom_unit
        ? { full: `${cd.custom_unit}`, prefix: '', suffix: cd.custom_unit }
        : helpers.getUnitSuffixes(cd.unit);
    const hasMultipleDatasets = cd.datasets.length > 1;
    // merge all the objects and build base configuration
    let pcd = {
        ...chartConfig.getBaseConfig(cd, hasMultipleDatasets),
        ...chartConfig.getAxisConfig(cd, suffixUnit),
        ...chartConfig.getTooltipConfig(suffixFullFormat, hasMultipleDatasets),
        ...chartConfig.getPlotOptionsConfig(cd, prefixUnit, suffixUnit),
    };

    // Add chart number to the configuration for accessibility
    pcd.chartNumber = cd.chartNumber;


    // Set colors for each chart type
    switch(cd.type) {
        case 'bar':
            if (cd.stacked === 1) {
                pcd.setArrayColors = settings['stackedBarColors'];
            } else if (cd.cagr === 1) {
                pcd.setArrayColors = settings['cagrBarColors'];
            } else {
                pcd.setArrayColors = settings['barColors'];
            }
        break;
        case 'doughnut':
        case 'pie' :
            pcd.setArrayColors = settings['colors'];
            pcd.plotOptions.series.colorByPoint = true;
        break;
        case 'line' :
            pcd.setArrayColors = settings['lineColors'];
            pcd.plotOptions.series.colorByPoint = true;
        break;
        default:
            pcd.setArrayColors = settings['colors'];
    }

    // Set up series data
    const dataSeries = [];
    if (hasMultipleDatasets) {
        pcd.plotOptions.series.colorByPoint = true;
        pcd.multiData = true;
        
        cd.datasets.forEach((value, index) => {
            const serieType = value.type === 'bar' ? 'column' : (value.type || pcd.chart.type);
            const thisIndex = value.type === 'bar' ? -1 : null;
            
            dataSeries[index] = {
                type: serieType,
                index: thisIndex,
                name: value.label,
                data: value.data,
                className: 'multidataset',
                color: value.custom_color || (pcd.plotOptions.series.colorByPoint === false 
                    ? pcd.setArrayColors[0] 
                    : pcd.setArrayColors[index]),
                colorByPoint: false,
                marker: {
                    symbol: settings.pointStyles[index],
                    fillColor: '#FFFFFF',
                    lineWidth: 2,
                    lineColor: null,
                    radius: 5
                }
            };
        });
    } else {
        const firstDataset = Array.isArray(cd.datasets)
            ? cd.datasets[0]
            : (cd.datasets && (cd.datasets[0] || cd.datasets["0"] || Object.values(cd.datasets)[0]));
        const firstData = firstDataset && Array.isArray(firstDataset.data) ? firstDataset.data : [];
        const customColors = Array.isArray(cd.custom_colors) ? cd.custom_colors : [];
        firstData.forEach((value, index) => {
            dataSeries[index] = {
                name: cd.labels[index],
                y: Number(value),
                color: customColors[index] || (pcd.plotOptions.series.colorByPoint === false
                    ? pcd.setArrayColors[0]
                    : pcd.setArrayColors[index])
            };
        });
    }

    pcd.series = [{
        name: cd.unit,
        innerSize: cd.type === 'doughnut' ? '35%' : '',
        data: cd.datasets.length === 1 ? dataSeries : undefined
    }];

    if (hasMultipleDatasets) {
        pcd.series = dataSeries;
    }

    // Handle CAGR chart configuration
    if (cd.type === 'bar' && cd.cagr) {
        const cagrDataset = Array.isArray(cd.datasets)
            ? cd.datasets[0]
            : (cd.datasets && (cd.datasets[0] || cd.datasets["0"] || Object.values(cd.datasets)[0]));
        const cagrData = cagrDataset && Array.isArray(cagrDataset.data) ? cagrDataset.data : [];
        const dataPoints = cagrData.length;
        // Mirrors PHP-side CAGR guards: first > 0, last >= 0, periods >= 1.
        // (neg / pos) ** fractional exponent yields NaN, which would render as "NaN%".
        if (dataPoints > 1 && Number(cagrData[0]) > 0 && Number(cagrData[dataPoints - 1]) >= 0) {
            const increment = (Number(cagrData[dataPoints - 1]) - Number(cagrData[0])) / (dataPoints - 1);
            const cagr = [];
            const idPoint = Math.floor(dataPoints/2);

            for (let i = 0; i < dataPoints; i++) {
                cagr[i] = i === 0 
                    ? { 
                        y: parseFloat(cagrData[i]),
                        accessibility: { enabled: false }
                    }
                    : { 
                        y: parseFloat((cagr[i - 1].y + increment).toFixed(2)), 
                        id: idPoint === i ? 'idPoint' : '',
                        accessibility: { enabled: false }
                    };
            }
            //this function is used to format the cagr value
            function formatCagrValue(value) {
                const periods = cagr.length - 1;
                const cagrValue = (Math.pow((cagr[periods].y / cagr[0].y), (1 / periods)) - 1) * 100;
                const formattedCagrValue = cagrValue % 1 === 0 
                    ? cagrValue.toFixed(0) 
                    : cagrValue.toFixed(2);
                return `${formattedCagrValue}%`;
            }
            //we need a custom accessibility for the cagr chart
            pcd.lang.accessibility.series = {
                summary: {
                    lineCombination: 'The CAGR value for this serie is: ' + formatCagrValue(this.y),
                    columnCombination: '' // No aria label for bar series
                }
            }
                
            pcd.annotations = [{
                draggable: false,
                labels: [{
                    point: 'idPoint',
                    style: { fontSize: '12px' },
                    formatter: function() {
                        return formatCagrValue(this.y) + ' CAGR';
                    }
                }],
                labelOptions: {
                    point: 'idPoint',
                    y: 16,
                    allowOverlap: true,
                    borderRadius: 10,
                    className: `cagr-label-${cd.id}`,
                    borderColor: '#80BBBF',
                    backgroundColor: '#80BBBF',
                    padding: 5,
                    style: { opacity: 0 },
                    useHTML: false,
                },
                events: {
                    click: function() {
                        return false;
                    }
                }
            }];

            pcd.series.push({
                type: 'line',
                name: 'CAGR',
                data: cagr,
                color: '#000',
                lineWidth: 4,
                label: { enabled: false },
                marker: { enabled: false },
                enableMouseTracking: false,
                accessibility: {
                    enabled: true,
                    point: {
                        enabled: false
                    }
                }
            });

            pcd.chart.events = {
                render() {
                    const containerId = this.container.id;
                    const chartData = this.series[0].data;
                    displayRotateLabel(containerId, chartData, cd.id);
                }
            };
        }
    }

    // Set chart options for highchart-helpers.js
    const legend_wrapper_id = `chart-${cd.row_id}-${cd.id}-legend`;
    const legendEl = document.getElementById(legend_wrapper_id);
    const legend_wrapper_type = legendEl ? legendEl.getAttribute('data-type') : null;
    
    pcd.options = {
        spanGaps: true,
        maintainAspectRatio: false,
        rotation: settings.rotation,
        cutout: cd.type === 'doughnut' ? settings.cutout : '0',
        unit: cd.unit,
        custom_unit: cd.custom_unit,
        chart_height: cd.chart_height,
        hide_x_axis_unit: cd.hide_x_axis_unit,
        hide_legend_unit: cd.hide_legend_unit,
        hide_tooltip_unit: cd.hide_tooltip_unit,
        tick_steps: cd.tick_steps || 50,
        extra_tick_space: cd.extra_tick_space,
        type: legend_wrapper_type,
        chartType: cd.type,
        xTitle: cd.x_axis_title
    };

    // Configure y-axis ticks
    if (cd.tick_steps && (cd.type === 'line' || cd.type === 'bar')) {
        const datasetsArr = Array.isArray(cd.datasets) ? cd.datasets : Object.values(cd.datasets || {});
        const validDatasets = datasetsArr.filter(d => d && Array.isArray(d.data) && d.data.length);
        if (validDatasets.length > 0) {
            const maxDataValue = Math.max(...validDatasets.map(d => Math.max(...d.data.map(Number))));
            if (Number.isFinite(maxDataValue)) {
                const addExtraSpace = cd.extra_tick_space === 1;
                pcd.yAxis.tickInterval = cd.tick_steps;
                pcd.yAxis.max = helpers.calculateYAxisMax(
                    maxDataValue,
                    cd.tick_steps,
                    addExtraSpace,
                    cd.stacked === 1
                );
            }
        }
    }
    return pcd;
}

// ============== Chart Initialization ==============
document.addEventListener("DOMContentLoaded", (event) => {
    const chart_groups = Array.from(document.querySelectorAll('.module-charts')).map((chart_group, index) => {
        const data = JSON.parse(document.getElementById(`charts-data-${chart_group.dataset.row}`).innerText);
        return { ...data, id: chart_group.dataset.row };
    });
    
    let chartCounter = 0; // Counter for consecutive chart numbers on the page
    
    chart_groups.forEach((charts_data) => {
        const row_id = charts_data.id;
        
        charts_data.all_data.forEach((cd) => {
            cd.row_id = row_id;
            const el = document.getElementById(`chart-${row_id}-${cd.id}`);
            
            if (el) {
                chartCounter++; // Increment counter for each chart
                cd.chartNumber = chartCounter; // Add chart number to the data
                
                // Set chart height
                const chart_height = cd.chart_height || {
                    pie: 800,
                    doughnut: 800,
                    bar: 340,
                    line: 600
                }[cd.type] || 600;
                
                el.height = chart_height;
                const legend_container_id = `chart-${cd.row_id}-${cd.id}-legend`; 
                // Create and configure chart
                const myChart = new Highcharts.Chart(el, prep_config(cd));
                
                // Accessibility fixes
                if (myChart.hasRendered) {
                    // Keeping this here for reference on how to access the chart container id in specific charts
                    // const containerWrapper = document.getElementById(myChart.container.id);
                    // const SRContainerIn = document.getElementById(`highcharts-end-of-chart-marker-${myChart.index}`);
                    removeTabindex('highcharts-exit-anchor');
                    fixElementRole('highcharts-exit-anchor', 'button');
                    addAccessibilityAttributes(myChart);
                }  
                
                // Set custom legend for non-bar charts or multi-dataset charts
                if (cd.type !== 'bar' || cd.datasets.length > 1) {
                    setCustomLegend(myChart, legend_container_id);
                }
            }
        });
    });
});